<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import ListingPagination from '@/components/listing-pagination'
import i18n from '@/i18n'
import { required, email, minLength } from 'vuelidate/lib/validators'
import { SHARED } from '@/constants'
import { ddlHelper, sharedHelper } from '@/utils'
import { inboxService } from '@/helpers/services/inbox.service'
import { tenantService } from '@/helpers/services/tenant.service'

/**
 * The team, as a CS supervisor uses it on shift.
 *
 * ── Why this still exists next to /users ────────────────────────────────
 * /users is the admin screen: spatie permissions, roles, and a first-time
 * password e-mail. This one needs NEITHER a mail server NOR a role — it is
 * gated on `users.is_admin` and it hands the password over directly. For a
 * brand with no SMTP configured, that is the only way to add somebody, and
 * removing it would have made this app unusable for the clients it was written
 * for. Four things, the ones that come up mid-shift: add someone, unlock
 * someone who fat-fingered their password, set a password, switch someone off.
 *
 * ── No delete ───────────────────────────────────────────────────────────
 * An agent's name is on every message they ever sent. Deleting the row leaves a
 * transcript signed by nobody, which is the one thing a payment dispute cannot
 * afford. Deactivate is the only exit.
 *
 * ── Why the paging is client-side ───────────────────────────────────────
 * `GET /v1/agents` deliberately returns the whole roster, because the inbox's
 * assignment picker is the same endpoint and needs every name at once. Paging
 * it server-side would have made that picker show the first thirty agents and
 * silently hide the rest. So the request stays whole and the table pages what
 * it already has — which is also why the filters below are instant.
 */
export default {
  components: { Layout, PageHeader, MultiSelect, ListingPagination },
  page: { title: i18n.t('module.team') },
  data() {
    return {
      title: i18n.t('module.team'),
      breadcrumbItems: [
        { text: i18n.t('module.inbox'), href: '/inbox' },
        { text: i18n.t('module.team'), active: true },
      ],

      agents: [],
      tenants: [],
      canManage: false,
      isPlatformStaff: false,

      loading: false,
      saving: false,
      error: '',
      message: '',

      // Filters, all multi-select. Empty means "no constraint" — which is why
      // there is no "All" option to deselect first.
      search: '',
      filterActive: [],
      filterTenants: [],
      showFilters: false,
      statusDdl: ddlHelper.getActivationStatusMulti(),

      currentPage: SHARED.DEFAULT_PAGE_NUMBER,
      perPage: SHARED.DEFAULT_PAGE_SIZE,

      showForm: false,
      form: { name: '', email: '', password: '', is_admin: false, tenant_ids: [] },

      pwFor: null,
      newPassword: '',
    }
  },
  computed: {
    fields() {
      return [
        { key: 'name', label: i18n.t('label.name') },
        { key: 'email', label: i18n.t('label.email') },
        { key: 'tenant', label: i18n.t('label.brand') },
        { key: 'state', label: i18n.t('label.status') },
        { key: 'action', label: i18n.t('label.action') },
      ]
    },
    tenantDdl() {
      return ddlHelper.getTenantOptions(this.tenants, true)
    },
    /** For the create form: no "platform" pseudo-option, because leaving the
     *  brand empty on a FORM already means a platform seat. */
    tenantFormDdl() {
      return ddlHelper.getTenantOptions(this.tenants, false)
    },
    filtered() {
      const needle = this.search.trim().toLowerCase()

      return this.agents.filter((a) => {
        if (needle && !`${a.name} ${a.email}`.toLowerCase().includes(needle)) return false

        if (this.filterActive.length) {
          const wanted = this.filterActive.map(Number)
          if (!wanted.includes(a.is_active ? 1 : 0)) return false
        }

        if (this.filterTenants.length) {
          // 'platform' selects the staff seats, which have NO brands —
          // the same sentinel the server's user filter uses, for the same
          // reason: an absence is not a value you can match against a list.
          const wantsPlatform = this.filterTenants.includes('platform')
          const ids = this.filterTenants.filter((v) => v !== 'platform').map(Number)
          const theirs = (a.tenant_ids || []).map(Number)

          // An agent on several brands matches a filter for any one of them.
          const matches =
            (theirs.length === 0 && wantsPlatform) ||
            theirs.some((id) => ids.includes(id))

          if (!matches) return false
        }

        return true
      })
    },
    /**
     * The rows on screen. Client-side slice — see the class note.
     *
     * `_showDetails` is b-table's own flag and is what makes the #row-details
     * slot render: the slot is NOT shown just because its template exists, so
     * without this the password box silently never appeared.
     */
    pagedRows() {
      const start = (this.currentPage - 1) * this.perPage

      return this.filtered
        .slice(start, start + this.perPage)
        .map((a) => ({ ...a, _showDetails: this.pwFor === a.id }))
    },
    /** Shaped like every other listing's, so the shared footer renders it. */
    pagination() {
      return {
        pageNumber: this.currentPage,
        pageSize: this.perPage,
        total: this.filtered.length,
        tableRowsCounts: this.pagedRows.length,
      }
    },
    activeFilterCount() {
      return [this.filterActive, this.filterTenants].filter((v) => v.length > 0).length
    },
    canSubmit() {
      return (
        this.form.name.trim() &&
        this.form.email.trim() &&
        this.form.password.length >= 10 &&
        !this.saving
      )
    },
  },
  watch: {
    // A narrowed list has fewer pages, and page 4 of two pages is an empty
    // table that reads as "no results".
    filtered() {
      const lastPage = Math.max(1, Math.ceil(this.filtered.length / this.perPage))
      if (this.currentPage > lastPage) this.currentPage = lastPage
    },
  },
  async created() {
    try {
      const result = await tenantService.list()
      this.tenants = result.data || []
      this.isPlatformStaff = !!result.is_platform_staff
    } catch (e) {
      // The brand column still renders from tenant_ids; only the names are lost.
    }

    await this.load()
  },
  /*
   * The same shape every other form in this app uses: a red border and a
   * message under the field.
   *
   * This one had neither — an empty name came back as a server error in a bar
   * at the top of the page, which says something is wrong and nothing about
   * which of the four boxes. The password rule is 10 characters because this
   * account can read customers' conversations.
   */
  validations: {
    form: {
      name: { required },
      email: { required, email },
      password: { required, minLength: minLength(10) },
    },
  },

  methods: {
    async load() {
      this.loading = true

      try {
        // `all=1` includes deactivated people — this screen needs them, the
        // assignment picker must not offer them.
        const result = await inboxService.getAgents(true)
        this.agents = result.data || []
        this.canManage = !!result.can_manage
        this.error = ''
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.loading = false
      }
    },

    resetFilters() {
      this.search = ''
      this.filterActive = []
      this.filterTenants = []
      this.currentPage = SHARED.DEFAULT_PAGE_NUMBER
    },

    /** A password nobody has to invent on the spot, and nobody reuses. */
    suggest() {
      const bytes = new Uint8Array(12)
      window.crypto.getRandomValues(bytes)
      this.form.password = Array.from(bytes, (b) => b.toString(36)).join('').slice(0, 16)
    },

    async create() {
      this.error = ''
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return

      this.saving = true

      try {
        const payload = {
          name: this.form.name.trim(),
          email: this.form.email.trim(),
          password: this.form.password,
          is_admin: this.form.is_admin,
        }

        // Only platform staff may choose; for anyone else the server takes the
        // brands from them, so sending them would be noise.
        if (this.isPlatformStaff) payload.tenant_ids = this.form.tenant_ids || []

        await inboxService.createAgent(payload)

        sharedHelper.alertSuccessMessage(i18n.t('team.createdWithPassword', [payload.name, payload.email]))
        this.showForm = false
        this.form = { name: '', email: '', password: '', is_admin: false, tenant_ids: [] }

        await this.load()
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.saving = false
      }
    },

    async patch(agent, payload, confirmText) {
      if (confirmText) {
        const ok = await sharedHelper.confirmQuestion({
          title: agent.name,
          text: confirmText,
          confirmText: i18n.t('label.confirm'),
          cancelText: i18n.t('label.cancel'),
        })

        if (!ok) return
      }

      this.error = ''

      try {
        await inboxService.updateAgent(agent.id, payload)
        await this.load()
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      }
    },

    async setPassword(agent) {
      if (this.newPassword.length < 10) {
        sharedHelper.alertErrorMessageWithoutCode(i18n.t('team.passwordTooShort'))
        return
      }

      await this.patch(agent, { password: this.newPassword })
      sharedHelper.alertSuccessMessage(i18n.t('team.passwordSetFor', [agent.name]))
      this.pwFor = null
      this.newPassword = ''
    },

    /** The brand cell. No brands means platform staff; several are joined,
     *  because covering two brands is the thing this column exists to show. */
    tenantNames(ids) {
      const list = (ids || []).map(Number)
      if (!list.length) return i18n.t('label.platformAllBrands')

      return list
        .map((id) => {
          const tenant = this.tenants.find((t) => Number(t.tenant_id) === id)
          return tenant ? tenant.name : i18n.t('label.brandN', [id])
        })
        .join(', ')
    },

    readError(e) {
      const data = e && e.response && e.response.data

      if (!data) return i18n.t('message.error')
      if (typeof data.error === 'string') return data.error
      // Laravel validation comes back as {field: [messages]}.
      if (data.error && typeof data.error === 'object') {
        return Object.values(data.error).flat().join(' ')
      }

      return i18n.t('message.error')
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />

    <div class="row">
      <div class="col-12">
        <div v-if="!canManage" class="alert alert-secondary py-2 small">
          {{ $t('team.readOnlyNotice') }}
        </div>
        <!--
          Outcomes are dialogs now (sharedHelper), the way the user form does
          it. What stays here is the LOAD failure: that describes the state of
          the page rather than the result of pressing something, and a dialog
          you dismiss would leave an empty list with no explanation.
        -->
        <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

        <div v-if="showForm" class="card mb-3">
          <div class="card-body">
            <b-row>
              <b-col cols="12" md="3" class="mb-2">
                <label class="small font-weight-bold mb-1">{{ $t('label.name') }}</label>
                <b-form-input
                  v-model="form.name"
                  size="sm"
                  :placeholder="$t('team.namePlaceholder')"
                  :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
                />
                <b-form-invalid-feedback>
                  {{ $t('message.requiredX', [$t('label.name')]) }}
                </b-form-invalid-feedback>
              </b-col>
              <b-col cols="12" md="3" class="mb-2">
                <label class="small font-weight-bold mb-1">{{ $t('label.email') }}</label>
                <b-form-input
                  v-model="form.email"
                  size="sm"
                  :placeholder="$t('user.emailHint')"
                  :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
                />
                <b-form-invalid-feedback>
                  {{ $v.form.email.required ? $t('user.emailInvalid') : $t('message.requiredX', [$t('label.email')]) }}
                </b-form-invalid-feedback>
              </b-col>
              <b-col cols="12" md="3" class="mb-2">
                <label class="small font-weight-bold mb-1">{{ $t('label.password') }}</label>
                <b-input-group size="sm">
                  <b-form-input
                    v-model="form.password"
                    :placeholder="$t('team.passwordPlaceholder')"
                    :state="$v.form.password.$dirty ? !$v.form.password.$error : null"
                  />
                  <b-input-group-append>
                    <b-button variant="outline-secondary" @click="suggest">{{ $t('team.suggest') }}</b-button>
                  </b-input-group-append>
                  <b-form-invalid-feedback :state="$v.form.password.$dirty ? !$v.form.password.$error : null">
                    {{ $t('team.passwordTooShort') }}
                  </b-form-invalid-feedback>
                </b-input-group>
              </b-col>
              <b-col v-if="isPlatformStaff" cols="12" md="3" class="mb-2">
                <label class="small font-weight-bold mb-1">{{ $t('label.brand') }}</label>
                <MultiSelect
                  v-model="form.tenant_ids"
                  :options="tenantFormDdl"
                  :multiple="true"
                  :placeholder="$t('label.platformAllBrands')"
                />
              </b-col>
            </b-row>

            <div class="d-flex justify-content-between align-items-center">
              <b-form-checkbox v-model="form.is_admin" switch>
                {{ $t('user.managesTeam') }}
              </b-form-checkbox>
              <b-button variant="primary" size="sm" :disabled="!canSubmit" @click="create">
                {{ saving ? $t('label.loading') : $t('label.submit') }}
              </b-button>
            </div>

            <!-- Said plainly, because the alternative is somebody waiting for
                 an e-mail that is never coming. The /users screen is the one
                 that mails a link. -->
            <p class="small text-muted mb-0 mt-2">{{ $t('team.notEmailedNotice') }}</p>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center w-100">
                  {{ $t('label.search') }}
                  <b-form-input
                    v-model="search"
                    type="search"
                    :placeholder="$t('label.search')"
                    class="form-control form-control-sm ml-2"
                  ></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <b-button variant="outline-secondary" size="sm" class="mr-2" @click="showFilters = !showFilters">
                  <i class="mdi mdi-filter-variant mr-1"></i>{{ $t('label.filter') }}
                  <b-badge v-if="activeFilterCount" variant="primary" class="ml-1">{{ activeFilterCount }}</b-badge>
                </b-button>
                <b-button v-if="canManage" variant="primary" size="sm" @click="showForm = !showForm">
                  <i class="mdi mdi-plus mr-1"></i>{{ showForm ? $t('label.cancel') : $t('team.addAgent') }}
                </b-button>
              </div>
            </div>

            <b-collapse v-model="showFilters">
              <div class="border rounded p-3 mb-3 bg-light">
                <b-row>
                  <b-col cols="12" md="4" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('label.status') }}</label>
                    <MultiSelect v-model="filterActive" :options="statusDdl" />
                  </b-col>
                  <b-col cols="12" md="8" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('label.brand') }}</label>
                    <MultiSelect v-model="filterTenants" :options="tenantDdl" />
                  </b-col>
                  <b-col cols="12" class="text-right">
                    <b-button variant="link" size="sm" @click="resetFilters">
                      {{ $t('label.clearFilters') }}
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </b-collapse>

            <div class="table-responsive mb-0">
              <b-table :items="pagedRows" :fields="fields" responsive="sm" :busy="loading" show-empty>
                <template #empty>
                  <p class="text-muted text-center my-3">{{ $t('label.noRecordFound') }}</p>
                </template>

                <template #cell(name)="data">
                  <span :class="{ 'text-muted': !data.item.is_active }">{{ data.item.name }}</span>
                  <b-badge v-if="data.item.is_admin" variant="info" class="ml-1">
                    {{ $t('user.managesTeam') }}
                  </b-badge>
                </template>

                <template #cell(email)="data">
                  <span class="small">{{ data.item.email }}</span>
                </template>

                <template #cell(tenant)="data">
                  <span class="small">{{ tenantNames(data.item.tenant_ids) }}</span>
                </template>

                <template #cell(state)="data">
                  <b-badge v-if="!data.item.is_active" variant="secondary">{{ $t('label.inactive') }}</b-badge>
                  <!-- Locked after repeated failed logins. Surfaced so an agent
                       does not need somebody with SSH to get back in. -->
                  <b-badge v-else-if="data.item.is_locked" variant="danger">{{ $t('label.lock') }}</b-badge>
                  <b-badge v-else variant="success">{{ $t('label.active') }}</b-badge>
                </template>

                <template #cell(action)="data">
                  <template v-if="canManage">
                    <b-button
                      v-if="data.item.is_locked"
                      variant="link"
                      size="sm"
                      @click="patch(data.item, { is_locked: false })"
                    >{{ $t('label.unlock') }}</b-button>
                    <b-button
                      variant="link"
                      size="sm"
                      @click="pwFor = pwFor === data.item.id ? null : data.item.id; newPassword = ''"
                    >{{ $t('label.password') }}</b-button>
                    <b-button
                      v-if="data.item.is_active"
                      variant="link"
                      size="sm"
                      class="text-danger"
                      @click="patch(data.item, { is_active: false }, $t('team.deactivateConfirm'))"
                    >{{ $t('team.deactivate') }}</b-button>
                    <b-button
                      v-else
                      variant="link"
                      size="sm"
                      @click="patch(data.item, { is_active: true })"
                    >{{ $t('team.reactivate') }}</b-button>
                  </template>
                </template>

                <!-- The password row expands under the agent it belongs to.
                     A b-table row-details slot rather than a trailing <tr>:
                     the old version appended one row to the end of the table,
                     so on a long roster the box appeared nowhere near the
                     person it was for. -->
                <template #row-details="data">
                  <div v-if="pwFor === data.item.id" class="d-flex align-items-center bg-light p-2">
                    <b-form-input
                      v-model="newPassword"
                      size="sm"
                      class="w-auto mr-2"
                      :placeholder="$t('team.passwordPlaceholder')"
                    />
                    <b-button variant="primary" size="sm" @click="setPassword(data.item)">
                      {{ $t('label.submit') }}
                    </b-button>
                    <span class="small text-muted ml-3">{{ $t('team.notEmailedNotice') }}</span>
                  </div>
                </template>
              </b-table>
            </div>

            <ListingPagination :pagination="pagination" @change="(p) => (currentPage = p)" />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
